import React from 'react';
import { useTranslation } from 'react-i18next';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';

function PartnerSettingsPageView({
  form,
  currencyOptions = [],
  isLoading,
  isSaving,
  error,
  success,
  formErrors,
  hasChanges,
  onFormChange,
  resetForm,
  saveForm,
}) {
  const { t } = useTranslation();
  const isCurrencyLocked = Boolean(form.walletCurrencyLocked);
  const minimumWithdrawalAmountErrorId = 'partner-minimum-withdrawal-amount-error';
  const minimumWithdrawalAmountHintId = 'partner-minimum-withdrawal-amount-hint';
  const walletCurrencyErrorId = 'partner-wallet-currency-error';
  const walletCurrencyWarningId = 'partner-wallet-currency-warning';
  const minimumWithdrawalAmountDescriptionIds = [
    minimumWithdrawalAmountHintId,
    formErrors.minimumWithdrawalAmount ? minimumWithdrawalAmountErrorId : '',
  ].filter(Boolean).join(' ');
  const walletCurrencyDescriptionIds = [
    formErrors.walletCurrency ? walletCurrencyErrorId : '',
    walletCurrencyWarningId,
  ].filter(Boolean).join(' ');

  return (
    <div>
      <FloatingAlerts
        info={isSaving ? t('partnerSettings.savingInfo') : ''}
        error={error}
        success={success}
      />
      <PageHeader title={t('partnerSettings.title')} subtitle={t('partnerSettings.subtitle')} />

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <LoadingContainer isLoading={isLoading} hasData className="">
            <form onSubmit={saveForm}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label htmlFor="partner-minimum-withdrawal-amount" className="form-label">
                    {t('partnerSettings.minimumWithdrawalAmountLabel')}
                  </label>
                  <input
                    id="partner-minimum-withdrawal-amount"
                    name="minimumWithdrawalAmount"
                    type="number"
                    min="1"
                    step="1"
                    className={`form-control ${formErrors.minimumWithdrawalAmount ? 'is-invalid' : ''}`}
                    value={form.minimumWithdrawalAmount}
                    onChange={(event) => onFormChange('minimumWithdrawalAmount', event.target.value)}
                    disabled={isSaving}
                    aria-invalid={formErrors.minimumWithdrawalAmount ? 'true' : undefined}
                    aria-describedby={minimumWithdrawalAmountDescriptionIds}
                    required
                  />
                  {formErrors.minimumWithdrawalAmount ? (
                    <div id={minimumWithdrawalAmountErrorId} className="invalid-feedback">
                      {formErrors.minimumWithdrawalAmount}
                    </div>
                  ) : null}
                  <div id={minimumWithdrawalAmountHintId} className="form-text">
                    {t('partnerSettings.minimumWithdrawalAmountHint')}
                  </div>
                </div>

                <div className="col-md-6">
                  <label htmlFor="partner-wallet-currency" className="form-label">
                    {t('partnerSettings.walletCurrencyLabel')}
                  </label>
                  <select
                    id="partner-wallet-currency"
                    name="walletCurrency"
                    className={`form-select ${formErrors.walletCurrency ? 'is-invalid' : ''}`}
                    value={form.walletCurrency}
                    onChange={(event) => onFormChange('walletCurrency', event.target.value)}
                    disabled={isSaving || isCurrencyLocked}
                    aria-invalid={formErrors.walletCurrency ? 'true' : undefined}
                    aria-describedby={walletCurrencyDescriptionIds || undefined}
                    required
                  >
                    {currencyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {formErrors.walletCurrency ? (
                    <div id={walletCurrencyErrorId} className="invalid-feedback">
                      {formErrors.walletCurrency}
                    </div>
                  ) : null}
                  <div id={walletCurrencyWarningId} className="text-warning small mt-2">
                    {t('partnerSettings.walletCurrencyLockedWarning')}
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={resetForm}
                  disabled={isSaving || !hasChanges}
                >
                  {t('partnerSettings.reset')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || isSaving || !hasChanges}
                >
                  {t('partnerSettings.saveChanges')}
                </button>
              </div>
            </form>
          </LoadingContainer>
        </div>
      </div>
    </div>
  );
}

export default PartnerSettingsPageView;
